// Chemin: C:\gestion-copro\src\services\pdfVerification.service.ts
// Session 69 : Génération rapport PDF vérification comptable
// Pattern basé sur pdfCourrier.service.ts

import jsPDF from 'jspdf';
import { Verification, Controle, TYPE_CONTROLE_LABELS, STATUT_CONTROLE_LABELS } from '../types/verification.types';

// ============================================================================
// CONFIGURATION PDF
// ============================================================================

const CONFIG = {
  pageWidth: 210,
  pageHeight: 297,
  marginTop: 20,
  marginBottom: 25,
  marginLeft: 20,
  marginRight: 20,
  lineHeight: 6,
  paragraphSpacing: 8,
  fontSizeTitle: 16,
  fontSizeSubtitle: 12,
  fontSizeNormal: 10,
  fontSizeSmall: 8,
};

// Couleurs
const COLORS = {
  primary: [30, 64, 175] as [number, number, number],      // Bleu Copro Protect
  success: [34, 197, 94] as [number, number, number],      // Vert
  warning: [245, 158, 11] as [number, number, number],     // Orange
  danger: [239, 68, 68] as [number, number, number],       // Rouge
  gray: [107, 114, 128] as [number, number, number],       // Gris
  lightGray: [243, 244, 246] as [number, number, number],  // Gris clair
  black: [0, 0, 0] as [number, number, number],
};

// ============================================================================
// TYPES
// ============================================================================

export interface PDFVerificationOptions {
  verification: Verification;
  controles: Controle[];
  nomCopropriete: string;
  documentsNoms: string[];
  generePar: string;
}

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(date: Date): string {
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateTime(date: Date): string {
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function cleanTextForPDF(text: string): string {
  if (!text) return '';
  return text
    .replace(/€/g, 'EUR')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/\u2026/g, '...')
    .replace(/\u2013/g, '-')
    .replace(/\u2014/g, '-')
    .replace(/≠/g, '!=')
    .replace(/[^\x00-\x7F\u00C0-\u00FF\u0152\u0153\u0178]/g, '');
}

function splitTextToLines(doc: jsPDF, text: string, maxWidth: number): string[] {
  const cleanedText = cleanTextForPDF(text);
  return doc.splitTextToSize(cleanedText, maxWidth);
}

function getScoreColor(score: number): [number, number, number] {
  if (score >= 90) return COLORS.success;
  if (score >= 70) return COLORS.warning;
  return COLORS.danger;
}

function formatMontant(montant: number): string {
  // Formatage compatible jsPDF (évite les espaces insécables)
  return montant.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' EUR';
}

function formatMontantSimple(montant: number): string {
  // Sans décimales pour les montants entiers
  return Math.round(montant).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' EUR';
}

function getResultatSymbol(resultat: string): string {
  if (resultat === 'ok') return '[OK]';
  if (resultat === 'attention') return '[!]';
  if (resultat === 'anomalie') return '[X]';
  return '[-]';
}

// ============================================================================
// GÉNÉRATION PDF
// ============================================================================

export const genererPDFVerification = async (
  options: PDFVerificationOptions
): Promise<Blob> => {
  const { verification, controles, nomCopropriete, documentsNoms, generePar } = options;
  const doc = new jsPDF('portrait', 'mm', 'a4');
  const contentWidth = CONFIG.pageWidth - CONFIG.marginLeft - CONFIG.marginRight;
  let yPos = CONFIG.marginTop;

  // ============================================
  // EN-TÊTE
  // ============================================
  
  // Titre principal
  doc.setFontSize(CONFIG.fontSizeTitle);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('COPRO PROTECT', CONFIG.marginLeft, yPos);
  
  // Sous-titre
  yPos += 8;
  doc.setFontSize(CONFIG.fontSizeSubtitle);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray);
  doc.text('Rapport de Verification Comptable', CONFIG.marginLeft, yPos);
  
  // Ligne de séparation
  yPos += 6;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(CONFIG.marginLeft, yPos, CONFIG.pageWidth - CONFIG.marginRight, yPos);
  
  yPos += 10;

  // ============================================
  // INFORMATIONS GÉNÉRALES
  // ============================================
  
  doc.setFontSize(CONFIG.fontSizeNormal);
  doc.setTextColor(...COLORS.black);
  
  // Copropriété
  doc.setFont('helvetica', 'bold');
  doc.text('Copropriete :', CONFIG.marginLeft, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(cleanTextForPDF(nomCopropriete), CONFIG.marginLeft + 30, yPos);
  yPos += CONFIG.lineHeight;
  
  // Exercice
  doc.setFont('helvetica', 'bold');
  doc.text('Exercice :', CONFIG.marginLeft, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(verification.exercice, CONFIG.marginLeft + 30, yPos);
  yPos += CONFIG.lineHeight;
  
  // Date du contrôle
  doc.setFont('helvetica', 'bold');
  doc.text('Date du controle :', CONFIG.marginLeft, yPos);
  doc.setFont('helvetica', 'normal');
  const dateControle = verification.dateModification?.toDate?.() || new Date();
  doc.text(formatDate(dateControle), CONFIG.marginLeft + 38, yPos);
  yPos += CONFIG.lineHeight;
  
  // Contrôlé par
  doc.setFont('helvetica', 'bold');
  doc.text('Controle par :', CONFIG.marginLeft, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(cleanTextForPDF(generePar), CONFIG.marginLeft + 32, yPos);
  yPos += CONFIG.lineHeight;
  
  // Titre/commentaire si présent
  if (verification.titre) {
    doc.setFont('helvetica', 'bold');
    doc.text('Commentaire :', CONFIG.marginLeft, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(cleanTextForPDF(verification.titre), CONFIG.marginLeft + 32, yPos);
    yPos += CONFIG.lineHeight;
  }
  
  yPos += CONFIG.paragraphSpacing;

  // ============================================
  // SCORE DE CONFORMITÉ
  // ============================================
  
  // Cadre score
  const scoreBoxHeight = 35;
  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(CONFIG.marginLeft, yPos, contentWidth, scoreBoxHeight, 3, 3, 'F');
  
  yPos += 8;
  doc.setFontSize(CONFIG.fontSizeSubtitle);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.black);
  doc.text('SCORE DE CONFORMITE', CONFIG.marginLeft + 5, yPos);
  
  // Score numérique
  yPos += 10;
  const scoreColor = getScoreColor(verification.scoreConformite);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...scoreColor);
  doc.text(`${verification.scoreConformite}/100`, CONFIG.marginLeft + 5, yPos);
  
  // Barre de progression
  const barreX = CONFIG.marginLeft + 45;
  const barreY = yPos - 6;
  const barreWidth = 80;
  const barreHeight = 8;
  
  // Fond de la barre
  doc.setFillColor(220, 220, 220);
  doc.roundedRect(barreX, barreY, barreWidth, barreHeight, 2, 2, 'F');
  
  // Progression
  const progressWidth = (verification.scoreConformite / 100) * barreWidth;
  doc.setFillColor(...scoreColor);
  doc.roundedRect(barreX, barreY, progressWidth, barreHeight, 2, 2, 'F');
  
  // Compteurs à droite
  const compteursX = CONFIG.marginLeft + 135;
  doc.setFontSize(CONFIG.fontSizeSmall);
  doc.setFont('helvetica', 'normal');
  
  doc.setTextColor(...COLORS.success);
  doc.text(`${verification.controlesOK} conformes`, compteursX, barreY + 2);
  
  doc.setTextColor(...COLORS.warning);
  doc.text(`${verification.controlesAttention} a verifier`, compteursX, barreY + 2 + CONFIG.lineHeight - 1);
  
  doc.setTextColor(...COLORS.danger);
  doc.text(`${verification.controlesAnomalie} anomalies`, compteursX, barreY + 2 + (CONFIG.lineHeight - 1) * 2);
  
  yPos += scoreBoxHeight - 8;
  yPos += CONFIG.paragraphSpacing;

  // ============================================
  // CONTRÔLES DÉTAILLÉS
  // ============================================
  
  doc.setTextColor(...COLORS.black);
  doc.setFontSize(CONFIG.fontSizeSubtitle);
  doc.setFont('helvetica', 'bold');
  doc.text('RESULTATS DES CONTROLES', CONFIG.marginLeft, yPos);
  yPos += CONFIG.paragraphSpacing;
  
  // Filtrer : anomalies et attention d'abord, puis OK
  const controlesAnomalies = controles.filter(c => c.resultat === 'anomalie');
  const controlesAttention = controles.filter(c => c.resultat === 'attention');
  const controlesOK = controles.filter(c => c.resultat === 'ok');
  const controlesOrdonnes = [...controlesAnomalies, ...controlesAttention, ...controlesOK];
  
  for (const controle of controlesOrdonnes) {
    // Vérifier si on a besoin d'une nouvelle page
    if (yPos > CONFIG.pageHeight - CONFIG.marginBottom - 30) {
      doc.addPage();
      yPos = CONFIG.marginTop;
    }
    
    // Symbole de résultat
    const symbol = getResultatSymbol(controle.resultat);
    let symbolColor: [number, number, number];
    if (controle.resultat === 'ok') symbolColor = COLORS.success;
    else if (controle.resultat === 'attention') symbolColor = COLORS.warning;
    else symbolColor = COLORS.danger;
    
    doc.setFontSize(CONFIG.fontSizeNormal);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...symbolColor);
    doc.text(symbol, CONFIG.marginLeft, yPos);
    
    // Type de contrôle
    doc.setTextColor(...COLORS.black);
    const typeLabel = TYPE_CONTROLE_LABELS[controle.type] || controle.type;
    doc.text(cleanTextForPDF(typeLabel), CONFIG.marginLeft + 12, yPos);
    
    // Statut à droite
    doc.setFontSize(CONFIG.fontSizeSmall);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray);
    const statutLabel = STATUT_CONTROLE_LABELS[controle.statut] || controle.statut;
    const statutWidth = doc.getTextWidth(statutLabel);
    doc.text(statutLabel, CONFIG.pageWidth - CONFIG.marginRight - statutWidth, yPos);
    
    yPos += CONFIG.lineHeight;
    
    // Description
    doc.setFontSize(CONFIG.fontSizeSmall);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.black);
    const descLines = splitTextToLines(doc, controle.description, contentWidth - 12);
    for (const line of descLines) {
      if (yPos > CONFIG.pageHeight - CONFIG.marginBottom - 10) {
        doc.addPage();
        yPos = CONFIG.marginTop;
      }
      doc.text(line, CONFIG.marginLeft + 12, yPos);
      yPos += CONFIG.lineHeight - 1;
    }
    
    // Montants si présents
    if (controle.montant !== undefined) {
      doc.setTextColor(...COLORS.gray);
      let montantText = `Montant : ${formatMontantSimple(controle.montant)}`;
      if (controle.montantAttendu !== undefined) {
        montantText += ` (attendu : ${formatMontantSimple(controle.montantAttendu)})`;
      }
      if (controle.ecart !== undefined) {
        montantText += ` - Ecart : ${formatMontantSimple(controle.ecart)}`;
      }
      doc.text(montantText, CONFIG.marginLeft + 12, yPos);
      yPos += CONFIG.lineHeight - 1;
    }
    
    // Règle appliquée
    if (controle.regleAppliquee) {
      doc.setFontSize(CONFIG.fontSizeSmall - 1);
      doc.setTextColor(...COLORS.gray);
      doc.setFont('helvetica', 'italic');
      const regleLines = splitTextToLines(doc, `Regle : ${controle.regleAppliquee}`, contentWidth - 12);
      for (const line of regleLines) {
        if (yPos > CONFIG.pageHeight - CONFIG.marginBottom - 10) {
          doc.addPage();
          yPos = CONFIG.marginTop;
        }
        doc.text(line, CONFIG.marginLeft + 12, yPos);
        yPos += CONFIG.lineHeight - 2;
      }
    }
    
    // Note CS si présente
    if (controle.noteCS) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.primary);
      const noteLines = splitTextToLines(doc, `Note CS : ${controle.noteCS}`, contentWidth - 12);
      for (const line of noteLines) {
        doc.text(line, CONFIG.marginLeft + 12, yPos);
        yPos += CONFIG.lineHeight - 1;
      }
    }
    
    yPos += CONFIG.paragraphSpacing / 2;
  }
  
  yPos += CONFIG.paragraphSpacing;

  // ============================================
  // DOCUMENTS ANALYSÉS
  // ============================================
  
  if (yPos > CONFIG.pageHeight - CONFIG.marginBottom - 40) {
    doc.addPage();
    yPos = CONFIG.marginTop;
  }
  
  doc.setTextColor(...COLORS.black);
  doc.setFontSize(CONFIG.fontSizeSubtitle);
  doc.setFont('helvetica', 'bold');
  doc.text('DOCUMENTS ANALYSES', CONFIG.marginLeft, yPos);
  yPos += CONFIG.paragraphSpacing;
  
  doc.setFontSize(CONFIG.fontSizeSmall);
  doc.setFont('helvetica', 'normal');
  
  if (documentsNoms.length === 0) {
    doc.setTextColor(...COLORS.gray);
    doc.text('Aucun document', CONFIG.marginLeft + 5, yPos);
    yPos += CONFIG.lineHeight;
  } else {
    for (const docNom of documentsNoms) {
      if (yPos > CONFIG.pageHeight - CONFIG.marginBottom - 10) {
        doc.addPage();
        yPos = CONFIG.marginTop;
      }
      doc.setTextColor(...COLORS.black);
      doc.text(`- ${cleanTextForPDF(docNom)}`, CONFIG.marginLeft + 5, yPos);
      yPos += CONFIG.lineHeight;
    }
  }

  // ============================================
  // PIED DE PAGE (toutes les pages)
  // ============================================
  
  const totalPages = doc.internal.getNumberOfPages();
  
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    // Ligne de séparation
    doc.setDrawColor(...COLORS.gray);
    doc.setLineWidth(0.2);
    doc.line(CONFIG.marginLeft, CONFIG.pageHeight - 18, CONFIG.pageWidth - CONFIG.marginRight, CONFIG.pageHeight - 18);
    
    // Texte pied de page
    doc.setFontSize(CONFIG.fontSizeSmall - 1);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray);
    
    // Gauche : signature
    doc.text('Genere par Copro Protect - SENTINELLE', CONFIG.marginLeft, CONFIG.pageHeight - 13);
    doc.setFont('helvetica', 'italic');
    doc.text('"La confiance n\'exclut pas le controle"', CONFIG.marginLeft, CONFIG.pageHeight - 9);
    
    // Centre : pagination
    doc.setFont('helvetica', 'normal');
    const pageText = `Page ${i}/${totalPages}`;
    const pageTextWidth = doc.getTextWidth(pageText);
    doc.text(pageText, (CONFIG.pageWidth - pageTextWidth) / 2, CONFIG.pageHeight - 11);
    
    // Droite : date génération
    const dateGen = formatDateTime(new Date());
    const dateWidth = doc.getTextWidth(dateGen);
    doc.text(dateGen, CONFIG.pageWidth - CONFIG.marginRight - dateWidth, CONFIG.pageHeight - 11);
  }

  return doc.output('blob');
};

// ============================================================================
// FONCTIONS EXPORT
// ============================================================================

export const telechargerPDFVerification = async (
  options: PDFVerificationOptions
): Promise<void> => {
  const blob = await genererPDFVerification(options);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Rapport_Verification_${options.verification.exercice}_${formatDate(new Date()).replace(/\//g, '-')}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const ouvrirPDFVerification = async (
  options: PDFVerificationOptions
): Promise<void> => {
  const blob = await genererPDFVerification(options);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
};
